/**
 * Content SCORM bridge — connects in-house SCORM launcher (postMessage) to cmi5Store SCORM adapter contract.
 * Maps scorm.* methods to SCORM 2004 CMI (GetValue/SetValue/Commit) via postMessage to parent launcher.
 * Used when content is loaded with bridgeToken (in-house launcher).
 */

/**
 * Invoke a method on the parent launcher via postMessage.
 * @param {Window} target - window.parent (launcher)
 * @param {string} parentOrigin - launcher origin for targetOrigin security
 * @param {string} method - e.g. 'GetValue', 'SetValue', 'Commit'
 * @param {Array} params - method arguments
 * @param {number} timeoutMs - max wait for response
 * @returns {Promise<string|boolean>}
 */
function invokeParent(target, parentOrigin, method, params = [], timeoutMs = 5000) {
    return new Promise((resolve, reject) => {
        const messageId = 'scorm-' + Date.now() + '-' + Math.random().toString(36).slice(2);
        let timer;
        const handler = (ev) => {
            if (ev.source !== target) return;
            if (parentOrigin !== '*' && ev.origin !== parentOrigin) return;
            const d = ev.data;
            if (!d || d.messageId !== messageId) return;
            clearTimeout(timer);
            window.removeEventListener('message', handler);
            if (d.error) {
                reject(new Error(d.error.message || 'SCORM bridge error'));
            } else {
                resolve(d.result);
            }
        };
        window.addEventListener('message', handler);
        target.postMessage({ messageId, method, params }, parentOrigin);
        timer = setTimeout(() => {
            window.removeEventListener('message', handler);
            reject(new Error('SCORM bridge timeout'));
        }, timeoutMs);
    });
}

/**
 * Format milliseconds as ISO 8601 duration (PTxHxMxS) for cmi.session_time.
 * @param {number} ms
 * @returns {string}
 */
function formatDuration(ms) {
    const totalSec = Math.floor(ms / 1000);
    const h = Math.floor(totalSec / 3600);
    const m = Math.floor((totalSec % 3600) / 60);
    const s = totalSec % 60;
    return `PT${h}H${m}M${s}S`;
}

/**
 * Create SCORM adapter that maps scorm.* methods to SCORM 2004 CMI via postMessage.
 * All operations are serialized via a promise queue to prevent race conditions.
 * @param {string} parentOrigin - launcher origin (from parentOriginHint or document.referrer)
 * @returns {Promise<Object>} adapter with SetScore, SetReachedEnd, SetPassed, SetFailed, ConcedeControl, GetSuspendData, SetSuspendData, GetStudentID, GetStudentName, GetLanguage
 */
const SS_START_KEY_BASE = 'awarego_scorm_session_start';

export function createScormAdapter(parentOrigin) {
    const target = window.parent;
    const cache = {};
    let sessionStartTime = null;
    // Scope session start key to this launch so two sessions in the same tab don't share timing
    const bridgeToken = new URLSearchParams(window.location.search).get('bridgeToken') || parentOrigin || 'default';
    const ssStartKey = `${SS_START_KEY_BASE}:${bridgeToken}`;

    // Serial operation queue — ensures postMessage calls never run concurrently
    let opQueue = Promise.resolve();
    function enqueue(fn) {
        const queued = opQueue.then(fn, fn);
        opQueue = queued.catch(() => {});
        return queued;
    }

    const getValue = (element) => invokeParent(target, parentOrigin, 'GetValue', [element]);
    const setValue = (element, value) => invokeParent(target, parentOrigin, 'SetValue', [element, String(value)]);
    const commit = () => invokeParent(target, parentOrigin, 'Commit', ['']);

    async function warmCache() {
        try {
            const flattened = await invokeParent(target, parentOrigin, 'getFlattenedCMI', [], 8000);
            if (flattened && typeof flattened === 'object') {
                Object.assign(cache, flattened);
            }
        } catch (e) {
            // Fallback: fetch key elements individually
            for (const key of [
                'cmi.suspend_data',
                'cmi.learner_id',
                'cmi.learner_name',
                'cmi.learner_preference.language',
            ]) {
                try {
                    const v = await getValue(key);
                    if (v != null && v !== '') cache[key] = v;
                } catch (_) { /* empty */ }
            }
        }

        // Tell the LMS this session is suspended so it doesn't auto-apply
        // completion rollup rules when the window closes without ConcedeControl.
        await setValue('cmi.exit', 'suspend');
        await commit();

        // Persist session start time so F5 reloads don't reset the timer.
        // On first warmCache: no key → record now. On subsequent warmCaches (after F5):
        // key exists → reuse original start so session_time stays cumulative.
        try {
            const stored = sessionStorage.getItem(ssStartKey);
            const parsed = stored ? parseInt(stored, 10) : NaN;
            sessionStartTime = !isNaN(parsed) ? parsed : Date.now();
            if (!stored) sessionStorage.setItem(ssStartKey, String(sessionStartTime));
        } catch (e) {
            sessionStartTime = Date.now();
        }
    }

    const adapter = {
        SetScore(raw, max, min) {
            enqueue(async () => {
                await setValue('cmi.score.raw', String(raw));
                if (max != null) await setValue('cmi.score.max', String(max));
                if (min != null) await setValue('cmi.score.min', String(min));
                if (max > 0 && raw != null) await setValue('cmi.score.scaled', String(Number(raw) / Number(max)));
                await commit();
            });
        },
        SetReachedEnd() {
            enqueue(async () => {
                await setValue('cmi.completion_status', 'completed');
                await setValue('cmi.progress_measure', '1');
                await commit();
            });
        },
        SetPassed() {
            enqueue(async () => {
                await setValue('cmi.success_status', 'passed');
                await commit();
            });
        },
        SetFailed() {
            enqueue(async () => {
                await setValue('cmi.success_status', 'failed');
                await commit();
            });
        },
        ConcedeControl() {
            enqueue(async () => {
                // Clear persisted start time — session is ending properly.
                try { sessionStorage.removeItem(ssStartKey); } catch (e) { /* empty */ }
                if (sessionStartTime != null) {
                    const duration = formatDuration(Date.now() - sessionStartTime);
                    await setValue('cmi.session_time', duration);
                }
                // Keep exit='suspend' so LMS always treats this as a suspend, not a final exit.
                await setValue('cmi.exit', 'suspend');
                await commit();
                await invokeParent(target, parentOrigin, 'Terminate', ['']);
            });
        },
        GetSuspendData() {
            return cache['cmi.suspend_data'] ?? '';
        },
        SetSuspendData(value) {
            cache['cmi.suspend_data'] = value;
            enqueue(async () => {
                await setValue('cmi.suspend_data', value);
                await commit();
            });
        },
        GetStudentID() {
            return cache['cmi.learner_id'] ?? '';
        },
        GetStudentName() {
            return cache['cmi.learner_name'] ?? '';
        },
        GetLanguage() {
            return cache['cmi.learner_preference.language'] ?? '';
        },
    };

    return warmCache().then(() => adapter);
}
